import { CommonModule } from '@angular/common';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, Input, Output, EventEmitter, inject, signal, computed } from '@angular/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { UploadModule, SelectEvent, RemoveEvent, SuccessEvent, ErrorEvent, UploadEvent } from '@progress/kendo-angular-upload';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { API } from '@mysrc/constants/api.constant';

export interface UploadedFile {
  file: File;
  name: string;
  progress: number;
}

@Component({
  selector: 'app-file-upload-dialog',
  standalone: true,
  imports: [
    CommonModule,
    UploadModule,
    ProgressBarModule
  ],
  templateUrl: './file-upload-dialog.component.html',
  styleUrl: './file-upload-dialog.component.scss',
})
export class FileUploadDialogComponent {
  @Input() files: UploadedFile[] = [];
  @Output() filesChange = new EventEmitter<UploadedFile[]>();

  // dialogRef = inject(DialogRef<FileUploadDialogComponent>);
  api = inject(HttpClient);

  // Kendo Upload URLs (can be dummy if handled manually)
  uploadUrl = '';
  removeUrl = '';

  isUploadComplete = computed(() =>
    this.files.every((file) => file.progress === 100)
  );

  // Kendo Upload: File(s) selected
  onFileSelected(event: SelectEvent) {
    const newFiles = event.files.map(f => ({
      file: f.rawFile as File,
      name: f.name,
      progress: 0
    }));
    this.files = [...this.files, ...newFiles];
    this.filesChange.emit(this.files);
  }

  // Kendo Upload: Remove file
  onRemoveFile(event: RemoveEvent) {
    const namesToRemove = event.files.map(f => f.name);
    this.files = this.files.filter(f => !namesToRemove.includes(f.name));
    this.filesChange.emit(this.files);
  }

  // Kendo Upload: Clear all files
  onClearFiles() {
    this.files = [];
    this.filesChange.emit(this.files);
  }

  // Kendo Upload: Upload success
  onUploadSuccess(event: SuccessEvent) {
    const fileName = event.files[0]?.name;
    this.files = this.files.map(f =>
      f.name === fileName ? { ...f, progress: 100 } : f
    );
    this.filesChange.emit(this.files);
  }

  // Kendo Upload: Upload error
  onUploadError(event: ErrorEvent) {
    const fileName = event.files[0]?.name;
    // Optionally handle error state
    console.error(`Upload failed for ${fileName}`);
  }

  // Remove file from custom list
  removeFile(index: number) {
    this.files.splice(index, 1);
    this.filesChange.emit(this.files);
  }

  // Start upload for all files (manual upload)
  syncFiles() {
    this.files.forEach((uploadedFile, index) => {
      const formData = new FormData();
      formData.append('file', uploadedFile.file, uploadedFile.name);

      this.api
        .post<any>(API.UPLOAD_ATTACHMENT, formData, {
          reportProgress: true,
          observe: 'events' as any,
        })
        .subscribe({
          next: (event: HttpEvent<any>) => {
            if (event.type === HttpEventType.UploadProgress) {
              const progress = Math.round(
                (100 * (event.loaded || 0)) / (event.total || 1)
              );
              this.files[index].progress = progress;
              this.filesChange.emit(this.files);
            } else if (event.type === HttpEventType.Response) {
              this.files[index].progress = 100;
              this.filesChange.emit(this.files);
            }
          },
          error: (err) => {
            console.error(`Upload failed for ${uploadedFile.name}:`, err);
          },
        });
    });
  }
}